import { ActivityType } from '@/components/Activities'
import { ErrorResponse, ResponseArray, ResponseObject } from '@/types/shared'
import datacentral from '@/utils/axios/datacentral'
import axios from 'axios'
import { TFunction } from 'i18next'
import qs from 'query-string'
import { DataGridPayload } from 'vnetwork-ui/dist/components/GokuDataGrid'
import {
    CreateTicketRequestPayload,
    DeleteTicketCommentPayload,
    TicketCommentPayload,
    TicketDetailPayload,
    TicketPayload,
    UpdateTicketCommentPayload,
    UpdateTicketRequestPayload,
} from '../types/payload'
import {
    AssigneeWorkload,
    AvgFirstResponseTime,
    CustomerSatisfaction,
    CustomerService,
    EscalatedTicketItem,
    FeedbackStats,
    FeedbackToken,
    FeedbackTokenResponse,
    MajorIncidentsSummary,
    MeetingTicketsByPurpose,
    OverdueTicketsByEmployee,
    SLACompliance,
    Ticket,
    TicketAttachments,
    TicketAvgPriority,
    TicketCreationTrend,
    TicketCustomerServices,
    TicketFeedback,
    TicketForFeedback,
    TicketHandler,
    TicketRequest,
    TicketResolutionRate,
    TicketStatusPeriod,
    TicketStatusSummary,
    TicketType,
    TicketsByEmployeeAndType,
    TicketsByProduct,
    TicketsBySource,
    TicketsByType,
    TopCustomerByTickets,
} from '../types/response'

export const getTicketRequest = async (): Promise<
    ResponseArray<TicketRequest>
> => {
    const response = await datacentral.get('/tickets/ticket_request')
    return response.data
}

export const getTicketRequestOptions = async () => {
    const response = await getTicketRequest()
    if (!response?.results) return []
    return response.results.map((item) => ({
        label: item?.ticket_request_title,
        value: item?.ticket_request_title,
    }))
}
export const getTicketRequestSelect = async () => {
    const response = await getTicketRequest()
    if (response?.results?.length === 0) return []
    return response.results.map((item) => ({
        label: item?.ticket_request_title,
        value: item?.ticket_request_id,
    }))
}

export const getTicketType = async (params?: {
    ticket_type_group?: string
}): Promise<ResponseArray<TicketType>> => {
    const query = params ? `?${qs.stringify(params)}` : ''
    const response = await datacentral.get(`/tickets/ticket_type${query}`)
    return response.data
}

export const getTicketTypeOptions = async (ticketTypeGroup?: string) => {
    const response = await getTicketType(
        ticketTypeGroup ? { ticket_type_group: ticketTypeGroup } : undefined
    )
    if (response?.results?.length === 0) return []
    return response.results.map((item) => ({
        label: item?.ticket_type_title,
        value: item?.ticket_type_id,
        ...item,
    }))
}

export const getTicketTypeFilterOptions = async () => {
    const response = await getTicketType()
    return response.results.map((item) => ({
        label: item?.ticket_type_title,
        value: item?.ticket_type_title,
    }))
}

export const getTicketTypeKeyFilterOptions = async () => {
    const response = await getTicketType()
    // Group by key and use first title as label (uppercase)
    const keyTitleMap = new Map<string, string>()
    response.results.forEach((item) => {
        if (item?.ticket_type_key && !keyTitleMap.has(item.ticket_type_key)) {
            keyTitleMap.set(
                item.ticket_type_key,
                item.ticket_type_title || item.ticket_type_key
            )
        }
    })
    return Array.from(keyTitleMap.entries()).map(([key, title]) => ({
        label: title.toUpperCase(),
        value: key,
    }))
}

interface CustomerServicesResponse {
    message: string
    result: {
        services: CustomerService[]
        service_interestings: { label: string; value: string }[]
    }
}

export const getCustomerServices = async (
    customerID: number,
    journeyID?: number
): Promise<CustomerServicesResponse> => {
    const params: Record<string, number> = { customerID }
    if (journeyID) {
        params.journeyID = journeyID
    }
    const response = await datacentral.get(
        `/tickets/customer_services?${qs.stringify(params)}`
    )
    return response.data
}

export const getCustomerServiceOptions = async (
    customerID: number,
    journeyID?: number
) => {
    const response = await getCustomerServices(customerID, journeyID)
    const services = response?.result?.services || []
    const serviceInterestings = response?.result?.service_interestings || []

    return {
        services: services.map((item) => ({
            label: `${item?.service_id} - ${item?.service_name}`,
            value: item?.service_id,
        })),
        serviceInterestings,
    }
}
// Fields that go directly as query params (not inside filters)
const BASE_FIELDS = ['page', 'page_size', 'sort_by', 'sort_order', 'q']

// Transform payload - wrap filter fields into base64 encoded JSON string
const transformTicketPayload = (
    payload: DataGridPayload & Record<string, any>
) => {
    const baseParams: Record<string, any> = {}
    const filterParams: Record<string, any> = {}
    let existingFilters: string | null = null

    Object.entries(payload).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return
        if (Array.isArray(value) && value.length === 0) return

        if (BASE_FIELDS.includes(key)) {
            baseParams[key] = value
        } else if (key === 'filters') {
            // Preserve existing encoded filters
            existingFilters = value as string
        } else {
            filterParams[key] = value
        }
    })

    // Merge existing filters with new filter params
    if (Object.keys(filterParams).length > 0) {
        let merged: Record<string, any> = {}
        if (existingFilters) {
            try {
                merged = JSON.parse(atob(existingFilters))
            } catch {
                // Ignore parse errors
            }
        }
        merged = { ...merged, ...filterParams }
        baseParams.filters = btoa(JSON.stringify(merged))
    } else if (existingFilters) {
        // Pass through existing encoded filters
        baseParams.filters = existingFilters
    }

    return baseParams
}

export const getTickets = async (
    payload: DataGridPayload & Record<string, any>
): Promise<ResponseArray<Ticket>> => {
    const transformedPayload = transformTicketPayload(payload)
    const response = await datacentral.get(
        `/tickets?${qs.stringify(transformedPayload)}`
    )
    return response.data
}

// meetings
export const getMeetings = async (
    payload: DataGridPayload
): Promise<ResponseArray<Ticket>> => {
    const response = await datacentral.get(
        `/tickets/meetings?${qs.stringify(payload)}`
    )
    return response.data
}

export const exportTickets = async (payload: DataGridPayload) => {
    try {
        const queryString = qs.stringify(payload)

        const response = await datacentral.get(
            `/tickets/export?${queryString}`,
            {
                responseType: 'blob',
            }
        )

        return response.data
    } catch (error) {
        console.error('Lỗi khi tải file Excel:', error)
        throw error
    }
}
export const getTicketByID = async (payload: TicketDetailPayload) => {
    const response = await datacentral.get<ResponseObject<Ticket>>(
        `/tickets/${payload.ticket_id}`
    )
    return response.data
}

export const createTicket = async ({
    payload,
}: {
    payload: CreateTicketRequestPayload
}): Promise<ResponseObject<TicketPayload> | ErrorResponse> => {
    const response = await datacentral.post<ResponseObject<TicketPayload>>(
        '/tickets',
        payload
    )
    return response.data
}

export const updateTicket = async ({
    payload,
}: {
    payload: UpdateTicketRequestPayload
}): Promise<ResponseObject<TicketPayload> | ErrorResponse> => {
    const response = await datacentral.put<ResponseObject<TicketPayload>>(
        `/tickets/${payload.note.ticket_id}`,
        payload
    )
    return response.data
}

export const getTicketLogs = async ({
    ticket_id,
    params,
}: {
    ticket_id: number
    params: Pick<DataGridPayload, 'page' | 'page_size'>
}): Promise<ResponseArray<ActivityType>> => {
    const response = await datacentral.get(
        `/tickets/${ticket_id}/logs?${qs.stringify(params)}`
    )
    return response?.data
}

export const getTicketAttachments = async ({
    ticket_id,
}: {
    ticket_id: number
}): Promise<ResponseArray<TicketAttachments>> => {
    const response = await datacentral.get(`/tickets/${ticket_id}/attachments`)
    return response?.data
}

export const createComment = async (
    payload: TicketCommentPayload
): Promise<ResponseArray<TicketCommentPayload>> => {
    const response = await datacentral.post(
        `/tickets/${payload.ticket_id}/comments`,
        payload
    )
    return response?.data
}

export const getTicketComments = async ({
    ticket_id,
}: {
    ticket_id: number
}): Promise<ResponseArray<TicketCommentPayload>> => {
    const response = await datacentral.get(`/tickets/${ticket_id}/comments?`)
    return response?.data
}

export const updateTicketComment = async (
    payload: UpdateTicketCommentPayload
) => {
    const response = await datacentral.put<
        ResponseObject<TicketCommentPayload>
    >(`/tickets/comment/${payload.ticket_comment_id}`, {
        ticket_comment_text: payload.ticket_comment_text,
    })
    return response.data
}

export const deleteTicketComment = async (
    payload: DeleteTicketCommentPayload
) => {
    const response = await datacentral.delete<
        ResponseObject<TicketCommentPayload>
    >(`/tickets/comment/${payload.ticket_comment_id}`)
    return response.data
}

export const getTicketOptions = async (query: string) => {
    const response = await datacentral.get<ResponseArray<Ticket>>(
        `/tickets/search?query=${query}`
    )
    if (!response?.data?.results) return []
    return response.data.results.map((item) => ({
        label: ['DC', 'Ticket', item?.ticket_id].join(' - '),
        value: item?.ticket_id,
    }))
}

export const getStatisticalTicketStatusSummary = async (params: {
    start_date: string
    end_date: string
}): Promise<ResponseArray<TicketStatusSummary>> => {
    const query = new URLSearchParams(
        params as Record<string, string>
    ).toString()
    const response = await datacentral.get(
        `/tickets/statistical_ticket_status_summary?${query}`
    )
    return response.data
}

export const getAvgResolutionTimeByPriority = async (params: {
    start_date: string
    end_date: string
}): Promise<ResponseArray<TicketAvgPriority>> => {
    const query = new URLSearchParams(
        params as Record<string, string>
    ).toString()
    const response = await datacentral.get(
        `/tickets/statistical_avg_resolution_time_by_priority?${query}`
    )
    return response.data
}

export const getTopTicketHandlers = async (params: {
    start_date: string
    end_date: string
}): Promise<ResponseArray<TicketHandler>> => {
    const query = new URLSearchParams(
        params as Record<string, string>
    ).toString()
    const response = await datacentral.get(
        `/tickets/statistical_top_ticket_handlers?${query}`
    )
    return response.data
}

export const getOverdueTicketsByEmployee = async (params: {
    start_date: string
    end_date: string
}): Promise<ResponseArray<OverdueTicketsByEmployee>> => {
    const query = new URLSearchParams(
        params as Record<string, string>
    ).toString()
    const response = await datacentral.get(
        `/tickets/statistical_overdue_tickets_by_employee?${query}`
    )
    return response.data
}

export const getTicketStatusCountByPeriod = async (params: {
    start_date: string
    end_date: string
    period?: 'day' | 'week' | 'month'
}): Promise<ResponseArray<TicketStatusPeriod>> => {
    const query = new URLSearchParams(
        params as Record<string, string>
    ).toString()

    const response = await datacentral.get(
        `/tickets/statistical_ticket_status_count_by_period?${query}`
    )

    return response.data
}

export interface StartTicketPayload {
    ticket_id: number
    ticket_time_response?: string | null // TG phản hồi, nếu không truyền thì lấy thời gian hiện tại
    ticket_technical_assignees?: number[] // Danh sách tech assignees, nếu không truyền thì lấy employee hiện tại
    reason?: string // Lý do bắt đầu xử lý (optional)
}

export const startTicketByID = async ({
    ticket_id,
    ticket_time_response,
    ticket_technical_assignees,
    reason,
}: StartTicketPayload): Promise<ResponseObject<Ticket>> => {
    const response = await datacentral.put(`/tickets/${ticket_id}/start`, {
        ticket_time_response,
        ticket_technical_assignees,
        reason,
    })
    return response?.data
}
export const resolvedTicketByID = async ({
    ticket_id,
}: {
    ticket_id: number
}): Promise<ResponseObject<Ticket>> => {
    const response = await datacentral.put(`/tickets/${ticket_id}/resolved`)
    return response?.data
}

export const rollbackTicketByID = async ({
    ticket_id,
    content,
}: {
    ticket_id: number
    content: string
}): Promise<ResponseObject<Ticket>> => {
    const response = await datacentral.put(`/tickets/${ticket_id}/rollback`, {
        content,
    })
    return response?.data
}

export const closedTicketByID = async ({
    ticket_id,
}: {
    ticket_id: number
}): Promise<ResponseObject<Ticket>> => {
    const response = await datacentral.put(`/tickets/${ticket_id}/closed`)
    return response?.data
}

// IT-ISMS: Escalate ticket per QT-01 Bước 5
export const escalateTicketByID = async ({
    ticket_id,
    reason,
    escalate_to,
}: {
    ticket_id: number
    reason: string
    escalate_to?: number[]
}): Promise<ResponseObject<Ticket>> => {
    const response = await datacentral.put(`/tickets/${ticket_id}/escalate`, {
        reason,
        escalate_to,
    })
    return response?.data
}

export const convertIncidentToProblem = async ({
    ticket_id,
    content,
}: {
    ticket_id: number
    content: string
}): Promise<ResponseObject<Ticket>> => {
    const response = await datacentral.put(
        `/tickets/${ticket_id}/convert_incident_to_problem`,
        {
            content,
        }
    )
    return response?.data
}

export const linkedTicketByID = async ({
    ticket_id,
    ticket_groups,
}: {
    ticket_id: number
    ticket_groups: string[]
}): Promise<ResponseObject<Ticket>> => {
    const response = await datacentral.put(`/tickets/${ticket_id}/linked`, {
        ticket_groups,
    })
    return response?.data
}

export const getTicketSelectOptions =
    (currentTicketId?: number, ticketGroups: string[] = []) =>
    async (query: string): Promise<{ label: string; value: any }[]> => {
        const response = await getTickets({ page: 1, page_size: 10, q: query })
        const tickets = response.results ?? []

        return tickets
            .filter(
                (ticket) =>
                    Number(ticket.ticket_id) !== Number(currentTicketId) &&
                    !ticketGroups.includes(String(ticket.ticket_id))
            )
            .map((ticket) => ({
                label: `Ticket ${ticket.ticket_id} - ${ticket.customer_name} - ${ticket.product_group_name}- (${ticket.ticket_type_title})`,
                value: String(ticket.ticket_id),
            }))
    }

// Get tickets filtered by ticket_type_group = error_service (for SLA downtime)
export const getErrorTicketSelectOptions =
    () =>
    async (query: string): Promise<{ label: string; value: any }[]> => {
        const response = await getTickets({
            page: 1,
            page_size: 10,
            q: query,
            ticket_type_group: 'error_service',
        })
        const tickets = response.results ?? []

        return tickets.map((ticket) => ({
            label: `#${ticket.ticket_id} - ${ticket.customer_name} - ${ticket.ticket_type_title}`,
            value: String(ticket.ticket_id),
        }))
    }

export const updatePublicTicketRating = async ({
    ticket_id,
    rating,
    feedback,
}: {
    ticket_id: number
    rating: number
    feedback?: string
}): Promise<ResponseObject<Ticket>> => {
    const response = await axios.put(
        `${import.meta.env.APP_API_URL}/tickets/public/${ticket_id}/rating`,
        {
            ticket_customer_rating: rating,
            ticket_customer_feedback: feedback || null,
        }
    )
    return response?.data
}

export const getPublicTicketRating = async (ticket_id: number) => {
    const response = await axios.get(
        `${import.meta.env.APP_API_URL}/tickets/public/${ticket_id}`
    )
    return response.data
}

// Get unassigned tickets
export const getUnassignedTickets = async (
    payload: DataGridPayload
): Promise<ResponseArray<Ticket>> => {
    const response = await datacentral.get(
        `/tickets/unassigned?${qs.stringify({ ...payload })}`
    )
    return response.data
}

// Assign ticket to employee(s)
export const assignTicket = async ({
    ticket_id,
    technical_assignees,
    reason,
}: {
    ticket_id: number
    technical_assignees: number[]
    reason?: string
}): Promise<ResponseObject<Ticket>> => {
    const response = await datacentral.post(
        `/tickets/${ticket_id}/assign-tech`,
        {
            technical_assignees,
            reason,
        }
    )
    return response?.data
}

// Bulk assign tickets to employee(s)
export const bulkAssignTickets = async ({
    ticket_ids,
    technical_assignees,
    reason,
}: {
    ticket_ids: number[]
    technical_assignees: number[]
    reason?: string
}): Promise<ResponseObject<{ success: number; failed: number }>> => {
    const response = await datacentral.post(`/tickets/bulk-assign-tech`, {
        ticket_ids,
        technical_assignees,
        reason,
    })
    return response?.data
}

//
export const getTicketCustomerServicesByCustomerID = async (
    t: TFunction,
    customerID: number,
    journeyID?: number
) => {
    const response = await datacentral.get<
        ResponseObject<TicketCustomerServices>
    >(
        `/tickets/customers/${customerID}/services?${
            journeyID ? `journey_id=${journeyID}` : ''
        }`
    )
    if (!response?.data?.result) return []
    const services = response?.data?.result?.services || []
    const serviceInterestings =
        response?.data?.result?.service_interestings || []
    return [
        ...services.map((service) => ({
            label: `${service?.service_id} - ${service?.product_group_name}${
                service?.is_poc ? ' - POC' : ''
            } - ${t(`services:service-status.${service?.service_status}`)}`,
            value: service?.service_id,
            type: 'service',
            ...service,
        })),
        ...serviceInterestings.map((item) => ({
            ...item,
            label: `${t('ticket.service-interestings')?.toUpperCase()} - ${
                item?.label
            }`,
            value: item?.value,
            type: 'service_interesting',
        })),
    ]
}

// ==================== Distribution Statistics ====================

export const getTicketsByType = async (params: {
    start_date: string
    end_date: string
}): Promise<ResponseArray<TicketsByType>> => {
    const query = new URLSearchParams(
        params as Record<string, string>
    ).toString()
    const response = await datacentral.get(
        `/tickets/statistical_tickets_by_type?${query}`
    )
    return response.data
}

export const getTicketsBySource = async (params: {
    start_date: string
    end_date: string
}): Promise<ResponseArray<TicketsBySource>> => {
    const query = new URLSearchParams(
        params as Record<string, string>
    ).toString()
    const response = await datacentral.get(
        `/tickets/statistical_tickets_by_source?${query}`
    )
    return response.data
}

export const getTicketsByProduct = async (params: {
    start_date: string
    end_date: string
    limit?: number
}): Promise<ResponseArray<TicketsByProduct>> => {
    const query = new URLSearchParams(
        params as unknown as Record<string, string>
    ).toString()
    const response = await datacentral.get(
        `/tickets/statistical_tickets_by_product?${query}`
    )
    return response.data
}

export const getTopCustomersByTickets = async (params: {
    start_date: string
    end_date: string
    limit?: number
}): Promise<ResponseArray<TopCustomerByTickets>> => {
    const query = new URLSearchParams(
        params as unknown as Record<string, string>
    ).toString()
    const response = await datacentral.get(
        `/tickets/statistical_top_customers_by_tickets?${query}`
    )
    return response.data
}

export const getMeetingTicketsByPurpose = async (params: {
    start_date: string
    end_date: string
}): Promise<ResponseArray<MeetingTicketsByPurpose>> => {
    const query = new URLSearchParams(
        params as Record<string, string>
    ).toString()
    const response = await datacentral.get(
        `/tickets/statistical_meeting_tickets_by_purpose?${query}`
    )
    return response.data
}

// ==================== Performance Statistics ====================

export const getAssigneeWorkload = async (params: {
    start_date: string
    end_date: string
}): Promise<ResponseArray<AssigneeWorkload>> => {
    const query = new URLSearchParams(
        params as Record<string, string>
    ).toString()
    const response = await datacentral.get(
        `/tickets/statistical_assignee_workload?${query}`
    )
    return response.data
}

export const getCustomerSatisfaction = async (params: {
    start_date: string
    end_date: string
}): Promise<ResponseObject<CustomerSatisfaction>> => {
    const query = new URLSearchParams(
        params as Record<string, string>
    ).toString()
    const response = await datacentral.get(
        `/tickets/statistical_customer_satisfaction?${query}`
    )
    return response.data
}

export const getSLACompliance = async (params: {
    start_date: string
    end_date: string
}): Promise<ResponseObject<SLACompliance>> => {
    const query = new URLSearchParams(
        params as Record<string, string>
    ).toString()
    const response = await datacentral.get(
        `/tickets/statistical_sla_compliance?${query}`
    )
    return response.data
}

export const getAvgFirstResponseTime = async (params: {
    start_date: string
    end_date: string
}): Promise<ResponseObject<AvgFirstResponseTime>> => {
    const query = new URLSearchParams(
        params as Record<string, string>
    ).toString()
    const response = await datacentral.get(
        `/tickets/statistical_avg_first_response_time?${query}`
    )
    return response.data
}

export const getTicketResolutionRate = async (params: {
    start_date: string
    end_date: string
}): Promise<ResponseObject<TicketResolutionRate>> => {
    const query = new URLSearchParams(
        params as Record<string, string>
    ).toString()
    const response = await datacentral.get(
        `/tickets/statistical_ticket_resolution_rate?${query}`
    )
    return response.data
}

export const getMajorIncidentsSummary = async (params: {
    start_date: string
    end_date: string
}): Promise<ResponseObject<MajorIncidentsSummary>> => {
    const query = new URLSearchParams(
        params as Record<string, string>
    ).toString()
    const response = await datacentral.get(
        `/tickets/statistical_major_incidents_summary?${query}`
    )
    return response.data
}

// ==================== Trend Statistics ====================

export const getTicketCreationTrend = async (params: {
    start_date: string
    end_date: string
    period?: 'day' | 'week' | 'month'
}): Promise<ResponseArray<TicketCreationTrend>> => {
    const query = new URLSearchParams(
        params as Record<string, string>
    ).toString()
    const response = await datacentral.get(
        `/tickets/statistical_ticket_creation_trend?${query}`
    )
    return response.data
}

// ==================== Feedback Token Management ====================

// Create a feedback token for a ticket (authenticated)
export const createFeedbackToken = async ({
    ticket_id,
    recipient_name,
    recipient_email,
    recipient_phone,
}: {
    ticket_id: number
    recipient_name?: string
    recipient_email?: string
    recipient_phone?: string
}): Promise<ResponseObject<FeedbackTokenResponse>> => {
    const response = await datacentral.post(
        `/tickets/${ticket_id}/feedback-token`,
        {
            recipient_name,
            recipient_email,
            recipient_phone,
        }
    )
    return response.data
}

// Get all feedback tokens for a ticket (authenticated)
export const getFeedbackTokensByTicketID = async (
    ticket_id: number
): Promise<ResponseArray<FeedbackToken>> => {
    const response = await datacentral.get(
        `/tickets/${ticket_id}/feedback-tokens`
    )
    return response.data
}

// Revoke a feedback token (authenticated)
export const revokeFeedbackToken = async (
    token_id: number
): Promise<ResponseObject<null>> => {
    const response = await datacentral.delete(
        `/tickets/feedback-token/${token_id}`
    )
    return response.data
}

// Get all feedbacks for a ticket (authenticated)
export const getFeedbacksByTicketID = async (
    ticket_id: number
): Promise<{
    message: string
    feedbacks: TicketFeedback[]
    stats: FeedbackStats
}> => {
    const response = await datacentral.get(`/tickets/${ticket_id}/feedbacks`)
    return response.data
}

// ==================== Public Feedback APIs (no auth) ====================

// Get ticket info by feedback token (public)
export const getTicketByFeedbackToken = async (
    token: string
): Promise<ResponseObject<TicketForFeedback>> => {
    const response = await axios.get(
        `${import.meta.env.APP_API_URL}/tickets/feedback/${token}`
    )
    return response.data
}

// Submit feedback via token (public)
export const submitFeedback = async ({
    token,
    rating_support_quality,
    rating_staff_attitude,
    feedback_text,
}: {
    token: string
    rating_support_quality: number
    rating_staff_attitude: number
    feedback_text?: string
}): Promise<ResponseObject<TicketFeedback>> => {
    const response = await axios.post(
        `${import.meta.env.APP_API_URL}/tickets/feedback/${token}`,
        {
            rating: rating_support_quality, // Legacy field for backward compatibility
            rating_support_quality,
            rating_staff_attitude,
            feedback_text,
        }
    )
    return response.data
}

// ==================== Ticket Processing by Employee and Type ====================

export const getTicketsByEmployeeAndType = async (params: {
    start_date: string
    end_date: string
}): Promise<ResponseArray<TicketsByEmployeeAndType>> => {
    const query = new URLSearchParams(
        params as Record<string, string>
    ).toString()
    const response = await datacentral.get(
        `/tickets/statistical_ticket_by_employee_and_ticket_type_group?${query}`
    )
    return response.data
}

// Drill-down for the escalated_tickets column above. Unlike the other
// statistical endpoints this one is permission-gated on Tickets:{index,show},
// so it can answer 403 to a user who can still read the counts.
export const getEscalatedTicketsByEmployee = async (params: {
    employee_id: number
    start_date: string
    end_date: string
    page: number
    page_size: number
}): Promise<ResponseArray<EscalatedTicketItem>> => {
    const query = new URLSearchParams({
        employee_id: String(params.employee_id),
        start_date: params.start_date,
        end_date: params.end_date,
        page: String(params.page),
        page_size: String(params.page_size),
    }).toString()
    const response = await datacentral.get(
        `/tickets/statistical_ticket_by_employee_escalated?${query}`
    )
    return response.data
}
